import { useEffect, useState } from "react";
import {
  CircleAlert,
  Database,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { api } from "../lib/api";
import { refreshFromServer } from "../lib/data";
import { useAuth } from "../lib/auth";
import {
  Card,
  EmptyState,
  InlineNotice,
  PageHeader,
  StatusBadge,
} from "../components/ui";

interface IntegrityIssue {
  type:
    | "INVENTORY_LEDGER_MISMATCH"
    | "LOT_LEDGER_MISMATCH"
    | "MONEY_LEDGER_MISMATCH"
    | "AVERAGE_COST_MISMATCH"
    | "UNVALUED_INVENTORY_INFLOW"
    | "SALE_BASE_QUANTITY_MISMATCH"
    | "SALE_LINE_TOTAL_MISMATCH"
    | "SALE_TOTAL_MISMATCH"
    | "SALE_SETTLEMENT_MISMATCH"
    | "RECEIPT_BASE_QUANTITY_MISMATCH"
    | "RECEIPT_BASE_UNIT_COST_MISMATCH"
    | "RECEIPT_LINE_TOTAL_MISMATCH"
    | "RECEIPT_TOTAL_MISMATCH"
    | "RECEIPT_SETTLEMENT_MISMATCH";
  entityId: string;
  entityName: string;
  expected: number;
  actual: number;
  unit?: string;
  message?: string;
}

interface IntegrityResult {
  ok: boolean;
  checkedAt: string;
  summary: {
    products: number;
    trackedProducts: number;
    moneyAccounts: number;
    sales: number;
    saleLines: number;
    receipts: number;
    receiptLines: number;
    issues: number;
  };
  issues: IntegrityIssue[];
}

const issueLabels: Record<IntegrityIssue["type"], string> = {
  INVENTORY_LEDGER_MISMATCH: "Tồn kho không khớp lịch sử",
  LOT_LEDGER_MISMATCH: "Tồn theo lô/HSD không khớp",
  MONEY_LEDGER_MISMATCH: "Số dư tiền không khớp lịch sử",
  AVERAGE_COST_MISMATCH: "Giá vốn bình quân không khớp replay sổ kho",
  UNVALUED_INVENTORY_INFLOW: "Tăng tồn chưa có giá vốn",
  SALE_BASE_QUANTITY_MISMATCH: "Quy đổi số lượng bán sang đơn vị cơ bản bị lệch",
  SALE_LINE_TOTAL_MISMATCH: "Thành tiền dòng bán không khớp số lượng × đơn giá",
  SALE_TOTAL_MISMATCH: "Tổng đơn bán không khớp tổng các dòng",
  SALE_SETTLEMENT_MISMATCH: "Thanh toán/công nợ đơn bán không khớp tổng đơn",
  RECEIPT_BASE_QUANTITY_MISMATCH: "Quy đổi số lượng nhập sang đơn vị cơ bản bị lệch",
  RECEIPT_BASE_UNIT_COST_MISMATCH: "Giá vốn đơn vị cơ bản không khớp quy cách nhập",
  RECEIPT_LINE_TOTAL_MISMATCH: "Thành tiền dòng nhập không khớp số lượng × giá nhập",
  RECEIPT_TOTAL_MISMATCH: "Tổng phiếu nhập không khớp tổng các dòng",
  RECEIPT_SETTLEMENT_MISMATCH: "Tiền trả/công nợ phiếu nhập không khớp tổng phiếu",
};

export function DataHealthPage() {
  const auth = useAuth();
  const [result, setResult] = useState<IntegrityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const canCheck =
    !auth.demo &&
    navigator.onLine &&
    ["OWNER", "MANAGER", "ACCOUNTANT"].includes(
      auth.session?.user.role ?? "",
    );

  async function check() {
    if (!canCheck) return;
    setLoading(true);
    setError("");
    try {
      const next = await api<IntegrityResult>("/integrity/check");
      setResult(next);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể kiểm tra dữ liệu",
      );
    } finally {
      setLoading(false);
    }
  }

  async function repairValuation() {
    const valuationIssues =
      result?.issues.filter(
        (issue) => issue.type === "AVERAGE_COST_MISMATCH",
      ) ?? [];
    if (
      valuationIssues.length === 0 ||
      (auth.session?.user.role ?? "") !== "OWNER"
    ) {
      return;
    }

    const reason = window.prompt(
      "BLedger sẽ chỉ sửa projection giá vốn bình quân từ lịch sử InventoryMovement; không sửa/xóa chứng từ gốc.\n\nNhập lý do đối chiếu:",
      "Đối chiếu lại giá vốn từ sổ kho",
    );
    if (reason == null) return;
    if (reason.trim().length < 3) {
      setError("Lý do đối chiếu cần ít nhất 3 ký tự.");
      return;
    }

    setRepairing(true);
    setError("");
    setMessage("");
    try {
      const response = await api<{
        repaired: number;
      }>("/integrity/reconcile-valuation", {
        method: "POST",
        body: JSON.stringify({
          idempotencyKey: crypto.randomUUID(),
          reason: reason.trim(),
        }),
      });
      await refreshFromServer();
      await check();
      setMessage(
        response.repaired > 0
          ? `Đã đối chiếu và sửa projection giá vốn cho ${response.repaired} SKU từ sổ kho.`
          : "Giá vốn hiện đã khớp sổ kho; không cần thay đổi.",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể đối chiếu lại giá vốn.",
      );
    } finally {
      setRepairing(false);
    }
  }

  useEffect(() => {
    void check();
  }, [canCheck]);

  const heroClass = result?.ok
    ? "integrity-hero integrity-ok"
    : "integrity-hero integrity-problem";

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Kiểm tra dữ liệu"
        description="Đối chiếu sổ kho, lô/HSD và số dư tiền với lịch sử giao dịch"
        action={
          canCheck ? (
            <button
              className="btn btn-secondary"
              disabled={loading}
              onClick={() => void check()}
            >
              <RefreshCw size={18} />
              {loading ? "Đang kiểm tra..." : "Kiểm tra lại"}
            </button>
          ) : undefined
        }
      />

      {auth.demo ? (
        <InlineNotice
          tone="info"
          title="Chế độ demo không chạy kiểm tra server"
        >
          Kiểm tra toàn vẹn cần dữ liệu authoritative trong PostgreSQL.
        </InlineNotice>
      ) : null}

      {!navigator.onLine && !auth.demo ? (
        <InlineNotice
          tone="warning"
          title="Cần kết nối mạng"
        >
          Kiểm tra toàn vẹn không dùng dữ liệu cache trên thiết bị.
        </InlineNotice>
      ) : null}

      {!["OWNER", "MANAGER", "ACCOUNTANT"].includes(
        auth.session?.user.role ?? "",
      ) ? (
        <InlineNotice
          tone="danger"
          title="Không có quyền"
        >
          Chỉ Chủ cửa hàng, Quản lý hoặc Kế toán được xem kiểm tra toàn vẹn.
        </InlineNotice>
      ) : null}

      {error ? (
        <InlineNotice tone="danger" title={error} />
      ) : null}
      {message ? (
        <InlineNotice tone="success" title={message} />
      ) : null}

      {result ? (
        <>
          <Card className={heroClass}>
            <div className="integrity-hero-icon">
              {result.ok ? (
                <ShieldCheck size={32} />
              ) : (
                <CircleAlert size={32} />
              )}
            </div>
            <div>
              <span>Kết quả đối chiếu</span>
              <h2>
                {result.ok
                  ? "Dữ liệu đang khớp"
                  : result.summary.issues + " vấn đề cần xử lý"}
              </h2>
              <small>
                Lần kiểm tra:{" "}
                {new Date(result.checkedAt).toLocaleString("vi-VN")}
              </small>
            </div>
            <StatusBadge tone={result.ok ? "success" : "danger"}>
              {result.ok ? "An toàn" : "Cần kiểm tra"}
            </StatusBadge>
          </Card>

          <div className="integrity-summary">
            <Card>
              <Database size={20} />
              <span>Sản phẩm</span>
              <strong>{result.summary.products}</strong>
            </Card>
            <Card>
              <ShieldCheck size={20} />
              <span>Theo dõi HSD</span>
              <strong>{result.summary.trackedProducts}</strong>
            </Card>
            <Card>
              <Database size={20} />
              <span>Tài khoản tiền</span>
              <strong>{result.summary.moneyAccounts}</strong>
            </Card>
            <Card>
              <Database size={20} />
              <span>Đơn bán / dòng</span>
              <strong>{result.summary.sales} / {result.summary.saleLines}</strong>
            </Card>
            <Card>
              <Database size={20} />
              <span>Phiếu nhập / dòng</span>
              <strong>{result.summary.receipts} / {result.summary.receiptLines}</strong>
            </Card>
          </div>

          {result.issues.some(
            (issue) => issue.type === "AVERAGE_COST_MISMATCH",
          ) ? (
            <InlineNotice
              tone="warning"
              title="Phát hiện lệch giá vốn bình quân"
            >
              <div style={{ display: "grid", gap: 10 }}>
                <span>
                  BLedger đã replay moving weighted average từ sổ kho bất biến.
                  Việc sửa chỉ cập nhật projection <code>Product.avgCost</code>;
                  chứng từ nhập/bán và InventoryMovement được giữ nguyên.
                </span>
                {(auth.session?.user.role ?? "") === "OWNER" ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={repairing}
                    onClick={() => void repairValuation()}
                  >
                    <RefreshCw size={18} />
                    {repairing
                      ? "Đang đối chiếu..."
                      : "Đối chiếu lại giá vốn từ sổ kho"}
                  </button>
                ) : null}
              </div>
            </InlineNotice>
          ) : null}

          {result.issues.length === 0 ? (
            <EmptyState
              title="Không phát hiện sai lệch"
              description="Tồn kho, lô/HSD, số dư tiền, công thức từng dòng bán/nhập và liên kết thanh toán/công nợ đều khớp dữ liệu authoritative."
            />
          ) : (
            <section>
              <div className="section-title">
                <div>
                  <CircleAlert size={20} />
                  <h2>Chi tiết sai lệch</h2>
                </div>
              </div>
              <Card className="integrity-list">
                {result.issues.map((issue) => (
                  <div
                    className="integrity-row"
                    key={issue.type + "-" + issue.entityId}
                  >
                    <CircleAlert size={20} />
                    <div>
                      <strong>{issue.entityName}</strong>
                      <small>{issueLabels[issue.type]}</small>
                      {issue.message ? (
                        <small>{issue.message}</small>
                      ) : (
                        <small>
                          Theo lịch sử:{" "}
                          {issue.expected.toLocaleString("vi-VN")}{" "}
                          {issue.unit ?? ""} · Hiện tại:{" "}
                          {issue.actual.toLocaleString("vi-VN")}{" "}
                          {issue.unit ?? ""}
                        </small>
                      )}
                    </div>
                    <StatusBadge tone="danger">Lệch</StatusBadge>
                  </div>
                ))}
              </Card>

              <InlineNotice
                tone="warning"
                title="Không tự sửa số dư"
              >
                BLedger chỉ phát hiện sai lệch. Không tự ghi đè tồn kho hoặc tiền để khớp số.
                Việc sửa phải dùng movement/reversal có lý do và audit.
              </InlineNotice>
            </section>
          )}
        </>
      ) : !loading && canCheck ? (
        <EmptyState
          title="Chưa có kết quả"
          description="Bấm Kiểm tra lại để chạy đối chiếu dữ liệu."
        />
      ) : null}
    </div>
  );
}
